// PATH: src/app/publicpages/JustCurious/JustCurious.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router";
import "../Home.scss"; // shared vars (--ink, --gold, --slate, fonts) + pub-nav
import "./JustCurious.scss";

// API — same calls the old inline Home.jsx tool-launcher used
import {
  getConstitutionParts,
  getConstitutionArticles,
  getGovernmentUpdates,
  getGovernmentMinisters,
  getArgumentDNA,
  argueDNAResult,
  getWebLatestNews,
} from "api/WebsiteApis/apis";

// Components
import KnowYourConstitution from "../components/KnowYourConstitution";
import TrackYourGovernment from "../components/TrackYourGovernment";
import ArgumentDNA from "../components/ArgumentDNA";
import HowWouldTheySeeIt from "../components/HowWouldTheySeeIt";

// ══════ JC_TOOLS — same 4 tiles, same icon/name/tag copy as the POC ══════
const JC_TOOLS = {
  const: {
    ico: "ti-scale",
    name: "Know Your Constitution",
    tag: "Go beyond the Preamble. Explore the Constitution.",
    tileDesc: "Know the rules that shape your rights.",
  },
  hwt: {
    ico: "ti-users",
    name: "How Would They See It",
    tag: "One headline. Three minds. Three different verdicts.",
    tileDesc: "Today's news through the lens of thinkers.",
  },
  quiz: {
    ico: "ti-sparkles",
    name: "Thinker In You",
    tag: "Answer a few questions on today\u2019s news. Meet the mind you think like.",
    tileDesc: "Which thinker do you argue like?",
  },
  govt: {
    ico: "ti-building-bank",
    name: "Track Your Government",
    tag: "What your government actually did this week.",
    tileDesc: "Real cabinet, real bills, plain language.",
  },
};
const JC_ORDER = ["const", "hwt", "quiz", "govt"];

// same saved theme as the landing page (navy unless the visitor chose light)
const readSavedTheme = () => {
  try {
    if (!localStorage.getItem("RAAJNEETI_NAVY_DEFAULT_V1")) return "navy";
    return localStorage.getItem("RAAJNEETI_LANDING_THEME") === "light" ? "light" : "navy";
  } catch {
    return "navy";
  }
};

export default function JustCurious() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toolParam = searchParams.get("tool");
  const activeTool = toolParam === "govt" ? null : toolParam; // null = browse grid

  useEffect(() => {
    document.body.classList.toggle("theme-navy", readSavedTheme() === "navy");
    return () => document.body.classList.remove("theme-navy");
  }, []);

  const [govtView, setGovtView] = useState("exec");
  const [govtOpen, setGovtOpen] = useState({});
  const [constOpenVideo, setConstOpenVideo] = useState({});
  const [quizStep, setQuizStep] = useState(-1);
  const [dnaAnswers, setDnaAnswers] = useState([]);
  const [dnaResult, setDnaResult] = useState(null);
  const [dnaResultLoading, setDnaResultLoading] = useState(false);

  const [constParts, setConstParts] = useState([]);
  const [constPartsLoading, setConstPartsLoading] = useState(false);
  const [selectedPartId, setSelectedPartId] = useState(null);
  const [constArticles, setConstArticles] = useState([]);
  const [constArticlesLoading, setConstArticlesLoading] = useState(false);

  const [govtUpdates, setGovtUpdates] = useState([]);
  const [govtUpdatesLoading, setGovtUpdatesLoading] = useState(false);
  const [govtMinisters, setGovtMinisters] = useState([]);
  const [govtMinistersLoading, setGovtMinistersLoading] = useState(false);

  const [dnaQuestions, setDnaQuestions] = useState([]);
  const [dnaLoading, setDnaLoading] = useState(false);

  const [hwtNews, setHwtNews] = useState(null);
  const [hwtLoading, setHwtLoading] = useState(false);
  useEffect(() => {
    const onMove = (e) => {
      const card = e.target.closest && e.target.closest(".jc-card");
      if (!card) return;
      const r = card.getBoundingClientRect();
      card.style.setProperty("--mx", `${e.clientX - r.left}px`);
      card.style.setProperty("--my", `${e.clientY - r.top}px`);
    };
    document.addEventListener("mousemove", onMove);
    return () => document.removeEventListener("mousemove", onMove);
  }, []);
  const jcOpen = (key) => {
    if (key === "govt") return;
    setSearchParams({ tool: key });
    window.scrollTo({ top: 0, behavior: "auto" });
  };
  const jcBack = () => {
    setSearchParams({});
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  const goToCA = () => {
    const newsId = hwtNews?.newsid ?? hwtNews?.NewsID ?? hwtNews?.id ?? null;
    const params = new URLSearchParams({ tab: "hwt" });
    if (newsId !== null && newsId !== undefined)
      params.set("newsid", String(newsId));
    navigate(`/raajneeti/current-affairs?${params.toString()}`);
  };
  const goToPublicHwt = () => {
    navigate("/how-would-they-see-it");
    window.scrollTo({ top: 0, behavior: "auto" });
  };
  const enterApp = () => navigate("/raajneeti/home");
  const exitApp = () => navigate("/");

  const toggleGovtSummary = (id) =>
    setGovtOpen((s) => ({ ...s, [id]: !s[id] }));
  const toggleConstVideo = (id) =>
    setConstOpenVideo((s) => ({ ...s, [id]: !s[id] }));

  const handleDnaAnswer = (questionId, optionId) => {
    const updated = [
      ...dnaAnswers,
      { questionid: questionId, optionid: optionId },
    ];
    setDnaAnswers(updated);
    const nextStep = quizStep + 1;
    setQuizStep(nextStep);
    if (nextStep === dnaQuestions.length) {
      setDnaResultLoading(true);
      argueDNAResult({ answers: updated })
        .then((res) => setDnaResult(res?.data?.thinker || null))
        .catch((err) => console.error("Error inside argueDNAResult:", err))
        .finally(() => setDnaResultLoading(false));
    }
  };

  const resetDnaQuiz = () => {
    setQuizStep(-1);
    setDnaAnswers([]);
    setDnaResult(null);
  };

  // Constitution API
  useEffect(() => {
    if (
      activeTool === "const" &&
      constParts.length === 0 &&
      !constPartsLoading
    ) {
      setConstPartsLoading(true);
      getConstitutionParts({})
        .then((res) => {
          const list = res?.data?.parts || [];
          const sorted = [...list].sort(
            (a, b) => a.DisplayOrder - b.DisplayOrder,
          );
          setConstParts(sorted);
          if (sorted.length > 0) setSelectedPartId(sorted[0].id);
        })
        .catch((err) =>
          console.error("Error inside getConstitutionParts:", err),
        )
        .finally(() => setConstPartsLoading(false));
    }
  }, [activeTool, constParts.length, constPartsLoading]);

  useEffect(() => {
    if (!selectedPartId) return;
    setConstArticlesLoading(true);
    setConstArticles([]);
    getConstitutionArticles({ partid: selectedPartId })
      .then((res) => setConstArticles(res?.data?.Articles || []))
      .catch((err) =>
        console.error("Error inside getConstitutionArticles:", err),
      )
      .finally(() => setConstArticlesLoading(false));
  }, [selectedPartId]);

  const selectedPart = useMemo(
    () => constParts.find((p) => p.id === selectedPartId) || null,
    [constParts, selectedPartId],
  );

  // Government API
  useEffect(() => {
    if (activeTool === "govt") {
      setGovtUpdatesLoading(true);
      getGovernmentUpdates({
        type: govtView === "exec" ? "Executive" : "Parliament",
      })
        .then((res) => setGovtUpdates(res?.data?.updates || []))
        .catch((err) =>
          console.error("Error inside getGovernmentUpdates:", err),
        )
        .finally(() => setGovtUpdatesLoading(false));
    }
  }, [activeTool, govtView]);

  useEffect(() => {
    if (
      activeTool === "govt" &&
      govtMinisters.length === 0 &&
      !govtMinistersLoading
    ) {
      setGovtMinistersLoading(true);
      getGovernmentMinisters({})
        .then((res) => {
          const list = res?.data?.ministers || [];
          const sorted = [...list].sort(
            (a, b) => a.DisplayOrder - b.DisplayOrder,
          );
          setGovtMinisters(sorted);
        })
        .catch((err) =>
          console.error("Error inside getGovernmentMinisters:", err),
        )
        .finally(() => setGovtMinistersLoading(false));
    }
  }, [activeTool, govtMinisters.length, govtMinistersLoading]);

  // Thinker In You API
  useEffect(() => {
    if (activeTool === "quiz" && dnaQuestions.length === 0 && !dnaLoading) {
      setDnaLoading(true);
      getArgumentDNA({})
        .then((res) => setDnaQuestions(res?.data?.questions || []))
        .catch((err) => console.error("Error inside getArgumentDNA:", err))
        .finally(() => setDnaLoading(false));
    }
  }, [activeTool, dnaQuestions.length, dnaLoading]);

  // How Would They See It API
  useEffect(() => {
    if (activeTool === "hwt" && !hwtNews && !hwtLoading) {
      setHwtLoading(true);
      getWebLatestNews({})
        .then((res) => setHwtNews(res?.data || null))
        .catch((err) => console.error("Error inside getWebLatestNews:", err))
        .finally(() => setHwtLoading(false));
    }
  }, [activeTool, hwtNews, hwtLoading]);

  const tool = activeTool ? JC_TOOLS[activeTool] : null;

  return (
    <div className="home-wrapper">
      <link
        href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,600;0,700;1,600&family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap"
        rel="stylesheet"
      />
      <link
        rel="stylesheet"
        href="https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@3.10.0/dist/tabler-icons.min.css"
      />

      <nav className="pub-nav" id="pub-nav">
        <div className="pub-nav-brand" onClick={exitApp}>
          Raajneeti <b>AI</b>
        </div>
        <div className="pub-nav-links">
          <button onClick={() => navigate("/just-curious")}>Explore</button>
          <button type="button" className="pub-nav-soon" aria-disabled="true">
            GS Current Affairs <span className="nav-soon-pill">Coming soon</span>
          </button>
          <button
            className="pub-nav-ai"
            onClick={() => navigate("/raajneeti/ai-samiksha")}
          >
            <i className="ti ti-sparkles"></i> AI समीक्षा
          </button>
        </div>
        <button className="pub-nav-cta" onClick={enterApp}>
          Learning PSIR <i className="ti ti-arrow-right"></i>
        </button>
      </nav>

      <div id="pg-justcurious" className="pg tool-section">
        {!activeTool && (
          <div id="jc-browse">
            <div className="sec-head-bar">
              <button className="jc-back" onClick={() => navigate("/")}>
                <i className="ti ti-arrow-left"></i> Back
              </button>
              <h2>
                <i className="ti ti-world"></i> Just Curious
              </h2>
              <p className="shb-tag">
                Go beyond the headline. From the Constitution to Government in
                action — explore it your way.
              </p>
              <div className="shb-meta">Free • No exam • Four tools</div>
            </div>
            <div className="jc-wrap">
              <div className="jc-grid" id="jc-grid">
                {JC_ORDER.map((key) => {
                  const t = JC_TOOLS[key];
                  return (
                    <button
                      key={key}
                      className={`jc-card${key === "govt" ? " soon" : ""}`}
                      id={`tile-${key}`}
                      onClick={() => jcOpen(key)}
                    >
                      {key === "govt" && (
                        <span className="soon-badge">Coming soon</span>
                      )}
                      <span className="jc-ico">
                        <i className={`ti ${t.ico}`}></i>
                      </span>
                      <h3>{t.name}</h3>
                      <p>{t.tileDesc}</p>
                      <span className="jc-open">
                        Open <i className="ti ti-arrow-right"></i>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activeTool && tool && (
          <div id="jc-tool" className="jc-toolview">
            <div className="sec-head-bar">
              <button className="jc-back" onClick={jcBack}>
                <i className="ti ti-arrow-left"></i> All tools
              </button>
              <h2>
                <i className={`ti ${tool.ico}`}></i> <span>{tool.name}</span>
              </h2>
              <p className="shb-tag">{tool.tag}</p>
            </div>
            <div className="jc-toolbody">
              {activeTool === "const" && (
                <div className="feature-panel on">
                  <KnowYourConstitution
                    constParts={constParts}
                    constPartsLoading={constPartsLoading}
                    selectedPartId={selectedPartId}
                    setSelectedPartId={setSelectedPartId}
                    constArticles={constArticles}
                    constArticlesLoading={constArticlesLoading}
                    selectedPart={selectedPart}
                    constOpenVideo={constOpenVideo}
                    toggleConstVideo={toggleConstVideo}
                  />
                </div>
              )}
              {activeTool === "hwt" && (
                <div className="feature-panel on">
                  <HowWouldTheySeeIt
                    hwtNews={hwtNews}
                    hwtLoading={hwtLoading}
                    goToCA={goToPublicHwt}
                  />
                </div>
              )}
              {activeTool === "quiz" && (
                <div className="feature-panel on">
                  <ArgumentDNA
                    dnaQuestions={dnaQuestions}
                    dnaLoading={dnaLoading}
                    dnaResult={dnaResult}
                    dnaResultLoading={dnaResultLoading}
                    quizStep={quizStep}
                    setQuizStep={setQuizStep}
                    handleDnaAnswer={handleDnaAnswer}
                    resetDnaQuiz={resetDnaQuiz}
                    goToCA={goToCA}
                  />
                </div>
              )}
              {activeTool === "govt" && (
                <div className="feature-panel on">
                  <TrackYourGovernment
                    govtView={govtView}
                    setGovtView={setGovtView}
                    govtUpdates={govtUpdates}
                    govtUpdatesLoading={govtUpdatesLoading}
                    govtMinisters={govtMinisters}
                    govtMinistersLoading={govtMinistersLoading}
                    govtOpen={govtOpen}
                    toggleGovtSummary={toggleGovtSummary}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 